/**
 * LA PANTALLA DE CIERRE — el vendedor voltea el teléfono (E5, 2026-08-22).
 *
 * Legible a un metro por una persona de 55 a 75 años (PRODUCT.md, Accessibility):
 * cuerpo grande, una sola pregunta —qué columna—, cero urgencia artificial. Lo que
 * ve el cliente es lo que el vendedor ya revisó en voz alta: columnas con la
 * recomendada marcada, «Solo esto» por rubro, el sello de vía, las líneas de
 * confianza que son ciertas, y dos salidas del mismo tamaño: enviar por WhatsApp
 * o firmar aquí. La propuesta completa sale por «Generar PDF» (no existe PDF como
 * archivo: es `window.print()`; en el PWA de iOS se abre en Safari para guardarlo).
 */

import { useMemo, useState } from 'react'
import { porCotizacion } from '../motor/calculo.js'
import { esOpciones, rotulo } from '../bloques.js'
import {
  rubrosDe, soloEsto, columnasActivas, importeDeColumna, recomendadaPorDefecto, viaDe,
  lineasDeConfianza, resumenWhatsApp, ligaWhatsApp
} from '../motor/cierre.js'
import { MARCA } from '../marca.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')

export default function Cierre({
  cot, cliente, ctx, objetos = {}, intervenciones = [], protocoloRevisionId,
  onRecomendar, onFirmar, onDocs, onAprobadoPorMensaje, atras, Barra
}) {
  const val = useMemo(() => porCotizacion(cot, ctx), [cot, ctx])
  const columnas = columnasActivas(cot, val)
  const recomendada = recomendadaPorDefecto(cot, val, objetos)
  const [rubroSolo, setRubroSolo] = useState(null)
  const rubros = rubrosDe(cot, ctx.P)
  const via = viaDe(cot, ctx.P)

  const revision = intervenciones.find(iv => iv.tipo === 'diagnostico' && protocoloRevisionId && iv.protocoloId === protocoloRevisionId)
  const hayDictamen = !!revision && Array.isArray(revision.respuestas) && revision.respuestas.length > 0
  const importeRecomendada = recomendada ? importeDeColumna(cot, val, recomendada) : 0
  const lineas = lineasDeConfianza({ hayRevision: !!revision, hayDictamen, importeRecomendada, marca: MARCA })

  const texto = resumenWhatsApp({ marca: MARCA, cliente, cot, val, recomendada, lineas })
  const liga = ligaWhatsApp(cliente?.telefono, texto)
  const valSolo = rubroSolo ? soloEsto(cot, rubroSolo, ctx) : null

  return (
    <div className="app cierre">
      <Barra titulo="Su propuesta" migaja={cot.nombre} atras={atras} />
      <main>
        {!columnas.length && (
          <div className="empty"><b>Todavía no hay nada que presentar.</b><p>Las columnas aparecen en cuanto haya partidas en alguna etapa.</p></div>
        )}

        {/* Las columnas: hasta tres, la recomendada marcada. En etapas el precio es el
            acumulado (1, 1+2, 1+2+3); en opciones son alternativas y no hay total sumado. */}
        <div className={`columnas n${columnas.length}`}>
          {columnas.map(e => (
            <button type="button" key={e} className={`columna ${e === recomendada ? 'recomendada' : ''}`}
              onClick={() => onRecomendar?.(e)} aria-pressed={e === recomendada}>
              {e === recomendada && <span className="pastilla">Recomendada</span>}
              <div className="columna-rotulo">{rotulo(cot, e)}</div>
              <div className="precio"><b>{mx(importeDeColumna(cot, val, e))}</b><i>MXN</i></div>
              <div className="s">
                {esOpciones(cot)
                  ? 'Una alternativa completa'
                  : e === 1 ? 'Lo que resuelve lo urgente' : e === 2 ? 'Lo esencial más la ampliación' : 'El proyecto completo'}
                {' · '}{val[e].dias} {val[e].dias === 1 ? 'día de obra' : 'días de obra'}
              </div>
            </button>
          ))}
        </div>
        {esOpciones(cot) && columnas.length > 1 && (
          <div className="s" style={{ margin: '10px 0 0' }}>Son alternativas: se contrata una. Los importes no se suman.</div>
        )}

        {/* La vía como sello */}
        {via.proyecto.length > 0 && (
          <div className="nota" style={{ marginTop: 'var(--e5)' }}>
            <b>Precio en firme</b> para lo anterior. <b>Estimado con rango, se firma anticipo de diseño</b> para:{' '}
            {via.proyecto.map(p => p.nombre).join(', ')} — su precio se entrega por separado tras el levantamiento de medidas.
          </div>
        )}
        {via.proyecto.length === 0 && columnas.length > 0 && (
          <div className="s" style={{ marginTop: 'var(--e4)' }}>Precio en firme: incluye material, instalación y transporte, con IVA.</div>
        )}

        {/* Solo esto — por rubro, con el cálculo completo */}
        {rubros.length > 1 && (
          <>
            <div className="sec">Por partes</div>
            {rubros.map(r => (
              <div className="card" key={r.rubro} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="grow">
                  <div className="t">{r.nombre}</div>
                  <div className="s">{r.partidas} {r.partidas === 1 ? 'partida' : 'partidas'}</div>
                </div>
                <button type="button" className={`ib sm ${rubroSolo === r.rubro ? 'pri' : ''}`}
                  onClick={() => setRubroSolo(rubroSolo === r.rubro ? null : r.rubro)}>Solo esto</button>
              </div>
            ))}
            {rubroSolo && valSolo && (
              <div className="nota acento">
                <b>Solo {rubros.find(r => r.rubro === rubroSolo)?.nombre}:</b>{' '}
                {columnasActivas(cot, valSolo).map(e => `${rotulo(cot, e)} ${mx(importeDeColumna(cot, valSolo, e))} MXN`).join(' · ') || 'sin partidas en ninguna etapa'}.
                {' '}Calculado como obra independiente — días, traslado y protección de rentabilidad incluidos.
              </div>
            )}
          </>
        )}

        {/* Líneas de confianza: solo las que son ciertas */}
        <div className="sec">Lo que respalda este precio</div>
        {lineas.map((l, i) => <div className="linea-confianza" key={i}>{l}</div>)}
        {!MARCA.domicilio && (
          <div className="warn noprint" style={{ marginTop: 'var(--e4)' }}>
            <b>Para el vendedor:</b> la propuesta aún no lleva el domicilio del proveedor (LFPC art. 52 lo exige en ventas a domicilio). Juan lo fija en <code>marca.js</code>.
          </div>
        )}

        {/* Dos salidas del mismo tamaño */}
        <div className="sec">Cómo seguimos</div>
        <div className="captura-acc" style={{ marginBottom: 'var(--e4)' }}>
          <a className="ib w" style={{ textAlign: 'center', lineHeight: '56px', textDecoration: 'none' }}
            href={liga} target="_blank" rel="noopener">Enviar por WhatsApp</a>
          <button type="button" className="ib pri w" onClick={onFirmar}>{cot.firmaCliente ? 'Ver la firma' : 'Firmar aquí'}</button>
        </div>
        <div className="captura-acc">
          <button type="button" className="ib" onClick={onDocs}>Generar PDF</button>
          <button type="button" className="ib" onClick={onAprobadoPorMensaje}
            disabled={!!cot.aprobacion}>{cot.aprobacion ? `Aprobó por ${cot.aprobacion.via} el ${(cot.aprobacion.en || '').slice(0, 10)}` : 'Aprobó por mensaje'}</button>
        </div>
        <div className="s" style={{ marginTop: 'var(--e4)' }}>
          La firma en pantalla y la aprobación por mensaje se registran por separado: no son lo mismo.
          El PDF se genera desde el navegador; en iPhone, ábrelo en Safari para guardarlo.
        </div>
      </main>
    </div>
  )
}
